import Controller, { inject as controller } from '@ember/controller';
import { service } from '@ember/service';
import WieSettings from 'eflex/constants/work-instructions/wie-settings';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import moment from 'moment-timezone';

export default class WorkInstructionsController extends Controller {
  @service workInstructionRepo;
  @service router;
  @service store;
  @service currentUser;
  @service notifier;
  @service intl;

  @tracked showCreateModal = false;
  @tracked autoVersion;
  @tracked newWorkInstruction;
  @tracked selectedPreset;

  @controller('workInstructions.index') WorkInstructionsIndexController;

  getStartedSizeCards = [
    {
      title: 'workInstructionEditor.ratio.43',
      ratio: '4:3',
      width: 1024,
      height: 768,
      type: WieSettings.getStartedTypes.FIXED,
    },
    {
      title: 'workInstructionEditor.ratio.169',
      ratio: '16:9',
      width: 1365,
      height: 768,
      type: WieSettings.getStartedTypes.FIXED,
    },
    {
      title: 'workInstructionEditor.ratio.custom',
      width: null,
      height: null,
      type: WieSettings.getStartedTypes.CUSTOM,
    },
  ];

  get canCreate() {
    return this.workInstructionRepo.userCanCreate(this.WorkInstructionsIndexController.selectedFolder);
  }

  get canCreateFolder() {
    return this.workInstructionRepo.userHasRootFolderEditor();
  }

  get writableFolders() {
    return this.workInstructionRepo.getWritableFolders();
  }

  get defaultFolder() {
    if (this.canCreateFolder) {
      return this.intl.t('none');
    } else {
      return null;
    }
  }

  create = task(waitFor(async () => {
    if (this.newWorkInstruction.name) {
      const dupeName = await this.store.queryRecord('work-instruction', {
        ids: [this.newWorkInstruction.id],
        name: this.newWorkInstruction.name,
      });

      if (dupeName) {
        this.notifier.sendError('workInstruction.duplicateName', { name: this.newWorkInstruction.name });
        return;
      }
    } else {
      await this.generateUntitledName(this.newWorkInstruction);
    }

    Object.assign(this, {
      selectedPreset: null,
      showCreateModal: false,
    });

    const createdWorkInstruction = this.workInstructionRepo.createWorkInstruction(this.newWorkInstruction);
    await this.router.transitionTo('workInstructions.workInstruction', createdWorkInstruction.id);
  }));

  async generateUntitledName(workInstruction) {
    let name = 'Untitled';
    let counter = 0;

    while (true) {
      const dupeName = await this.store.queryRecord('work-instruction', {
        ids: [workInstruction.id],
        name,
      });

      if (!dupeName) {
        break;
      }

      counter++;
      name = `Untitled${counter}`;
    }

    workInstruction.name = name;
  }

  selectPreset = (params) => {
    this.selectedPreset = params;
    this.newWorkInstruction.width = params.width;
    this.newWorkInstruction.height = params.height;
  };

  createStart = () => {
    this.newWorkInstruction = this.store.createRecord('work-instruction', {
      authorUsername: this.currentUser.user?.userName,
      folder: this.WorkInstructionsIndexController.selectedFolder,
      name: null,
    });

    Object.assign(this, {
      autoVersion: moment().format('YYYYMMDDHHmmss'),
      selectedPreset: null,
      showCreateModal: true,
    });
  };

  cancelNew = () => {
    this.showCreateModal = false;
  };
}
